// src/components/hire/PaymentFlow/OrderSummary/OrderSummary.jsx
import React, { useContext, useState } from 'react'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import { OrderSummarySkeleton } from './skeletons/OrderSummarySkeleton'
import * as Popover from '@radix-ui/react-popover'
import { SharedContext } from '@/context/SharedContext'

export const OrderSummary = ({ summary, isLoading }) => {
  const { windowSize } = useContext(SharedContext)
  const isDesktop = windowSize > 768
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  if (isLoading) {
    return <OrderSummarySkeleton />
  }
  if (!summary) return null

  const { title, units, price } = summary?.package?.[0]
  const highlights = summary?.package?.[0]?.highlights ?? []

  const openTooltip = () => setIsTooltipOpen(true)
  const closeTooltip = () => setIsTooltipOpen(false)

  return (
    <div className="flex flex-col gap-3 rounded-sm border-[1px] border-solid border-[#cbcbcb] py-24 px-16">
      <div id="summary_title" className="text-base font-bold leading-21 text-[#323232]">
        {summary?.title}
      </div>
      <div className="border-xs border-dashed border-[#e5e5e5]"></div>
      <div id="package_details" className="flex items-center justify-between">
        <div id="package_title_container" className="flex items-center gap-1">
          <div id="title_icon" className="flex items-center gap-[2px]">
            <div id="title" className="text-base leading-21 text-[#323232]">
              {title}
            </div>
            <Popover.Root open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
              <Popover.Trigger asChild>
                <ErrorOutlineOutlinedIcon
                  className="h-[18px] w-[18px] cursor-pointer text-[#4B4B4B]"
                  {...(isDesktop
                    ? {
                        onMouseEnter: openTooltip,
                        onMouseLeave: closeTooltip
                      }
                    : {
                        onClick: () => setIsTooltipOpen((p) => !p)
                      })}
                />
              </Popover.Trigger>

              <Popover.Portal>
                <Popover.Content
                  side="top"
                  align="start"
                  sideOffset={4}
                  className="data-[state=open]:animate-fade-in z-50
                             flex w-[280px] max-w-[240px] flex-col gap-2 rounded-sm bg-white p-16 shadow-[0_8px_28px_rgba(0,0,0,0.12)] sm:w-[336px]"
                  onPointerDownOutside={() => {
                    if (!isDesktop) setIsTooltipOpen(false)
                  }}
                >
                  <div className="text-md font-bold leading-18 text-title">{title}</div>
                  <ul className="mb-[0px] list-disc space-y-[6px] pl-[20px] marker:text-sm">
                    {highlights.map((h) => (
                      <li key={h.id} className="text-base leading-24 text-[#323232]">
                        {h.description}
                      </li>
                    ))}
                  </ul>
                  <Popover.Arrow className="fill-white drop-shadow-md" />
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          </div>
          {units > 1 && (
            <div
              id="units_multiply"
              className="flex items-center gap-[1px] text-base font-bold leading-21 text-title"
            >
              <div>x</div>
              <div>{units}</div>
            </div>
          )}
        </div>
        <div id="package_price" className="text-md leading-24 text-title">
          {price}
        </div>
      </div>
      <div className="border-xs border-dashed border-[#e5e5e5]"></div>
      <div id="discount_container" className="flex items-center justify-between">
        <div className="text-base leading-21 text-[#323232]">
          Discount ({summary?.discount?.percent})
        </div>
        <div className="text-md leading-24 text-[#028A12]">{summary?.discount?.amount}</div>
      </div>
      <div className="border-xs border-dashed border-[#e5e5e5]"></div>
      <div id="subtotal_container" className="flex items-center justify-between">
        <div className="text-base leading-21 text-[#323232]">Sub Total</div>
        <div className="text-md font-bold leading-24 text-title">{summary?.sub_total}</div>
      </div>
      <div id="gst_container" className="flex items-center justify-between">
        <div className="text-base leading-21 text-[#323232]">GST ({summary?.gst?.percent})</div>
        <div className="text-md font-normal leading-24 text-title">{summary?.gst?.amount}</div>
      </div>
      <div className="border-xs border-dashed border-[#e5e5e5]"></div>

      <div id="total_bill_container" className="flex items-center justify-between">
        <div className="text-md font-bold leading-24 text-title">Your Total Bill</div>
        <div className="text-lg font-bold leading-27 text-title">{summary?.total_amount}</div>
      </div>
      <div className="text-right text-[11px] text-[#989898]">The prices are inclusive of GST</div>
    </div>
  )
}
